import { Building2, MapPin, Filter, X, ChevronDown } from "lucide-react";
import {
  useInventoryFilter,
  type AssetType,
  type Status,
} from "@/lib/inventory-filter";

const typeOptions: { id: AssetType; label: string }[] = [
  { id: "all", label: "Kaikki tyypit" },
  { id: "Toimisto", label: "Toimisto" },
  { id: "Liiketila", label: "Liiketila" },
  { id: "Logistiikka", label: "Logistiikka" },
  { id: "Kampus", label: "Kampus" },
  { id: "Asuin", label: "Asuin" },
];

const statusOptions: { id: Status; label: string }[] = [
  { id: "all", label: "Kaikki tilat" },
  { id: "ok", label: "Optimoitu" },
  { id: "warn", label: "Potentiaalia" },
  { id: "alert", label: "Toimi nyt" },
];

export function InventoryFilters({
  hideStatusFacet = false,
}: {
  /** Hide the Optimoitu / Potentiaalia / Toimi nyt status select — that facet encodes
   *  optimization status, which has no meaning on a setup lens (Konfiguraatio portfolio-scope).
   *  City and type stay. */
  hideStatusFacet?: boolean;
} = {}) {
  const {
    filter,
    setCity,
    setType,
    setStatus,
    reset,
    isFiltered,
    countLabel,
    cityFilterOptions,
  } = useInventoryFilter();

  return (
    <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1.5 pr-2 mr-1 border-r border-border/60 text-[11px] uppercase tracking-[0.16em] text-muted-foreground shrink-0">
        <Filter className="h-3.5 w-3.5" />
        Rajaa
      </div>

      <Select
        icon={MapPin}
        value={filter.city}
        options={cityFilterOptions}
        onChange={setCity}
      />
      <Select
        icon={Building2}
        value={filter.type}
        options={typeOptions}
        onChange={(v) => setType(v as AssetType)}
      />
      {!hideStatusFacet && (
        <Select
          value={filter.status}
          options={statusOptions}
          onChange={(v) => setStatus(v as Status)}
        />
      )}

      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-muted-foreground">{countLabel}</span>
        {isFiltered && (
          <button
            onClick={reset}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] hover:border-primary/40 transition"
          >
            <X className="h-3 w-3" />
            Tyhjennä
          </button>
        )}
      </div>
    </div>
  );
}

function Select<T extends string>({
  icon: Icon,
  value,
  options,
  onChange,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="relative inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs hover:border-primary/40 transition">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-transparent outline-none pr-5 appearance-none text-foreground"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="h-3 w-3 text-muted-foreground pointer-events-none absolute right-2" />
    </label>
  );
}
